import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCourses, downloadCertificate } from "@/services/api";
import Layout from "@/components/Layout";
import { Award, Download, ChevronLeft, CheckCircle2 } from "lucide-react";

export default function CertificatePage() {
  const { courseId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const decodedId = decodeURIComponent(courseId);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCourses(token, user.id);
        const found = (res.data || []).find((c) => c.course_identifier === decodedId);
        setCourse(found || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, token, user, decodedId]);

  const handleDownload = async () => {
    if (!course?.assignment_id) {
      setError("A bizonyítvány letöltéséhez az azonosító hiányzik.");
      return;
    }
    setDownloading(true);
    setError("");
    try {
      const res = await downloadCertificate(token, course.assignment_id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `naturtrak_bizonyitvany_${course.assignment_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Certificate download error:", e);
      const errorMsg = e.message || "A bizonyítvány letöltése nem sikerült. Kérjük próbálja újra.";
      setError(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64" style={{ color: "#242220" }}>
          <p className="font-bold opacity-40">Betöltés...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="certificate-page">
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center gap-2 mb-6 font-bold text-sm"
          style={{ color: "#242220", background: "none", border: "none", cursor: "pointer" }}
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
          Vissza
        </button>

        <div
          className="max-w-lg mx-auto"
          style={{
            background: "#fff",
            borderRadius: "24px",
            border: "1.5px solid #242220",
            boxShadow: "8px 8px 0 #242220",
            overflow: "hidden",
          }}
        >
          {/* Top celebration image */}
          <div
            style={{
              background: "#A1E1B9",
              padding: "32px",
              borderBottom: "1.5px solid #242220",
              textAlign: "center",
            }}
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/da1a3332-a31e-4aa2-9b69-b66190a6faa2/images/12948c93c507a612d52d6de79a642bfee177a8939fdc1ad34b164ec109c2baf0.png"
              alt="Gratulálunk"
              style={{
                width: 180,
                height: 180,
                objectFit: "cover",
                borderRadius: "24px",
                border: "1.5px solid #242220",
                margin: "0 auto",
              }}
            />
          </div>

          <div className="p-8 text-center">
            <div
              className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "#FBD556",
                borderRadius: "20px",
                border: "1.5px solid #242220",
              }}
            >
              <Award size={28} strokeWidth={2.5} color="#242220" />
            </div>

            <h1 className="text-3xl font-bold mb-2" style={{ color: "#242220" }}>
              Gratulálunk!
            </h1>
            <p className="text-base mb-4" style={{ color: "#242220", opacity: 0.7 }}>
              Sikeresen teljesítetted a kurzust
            </p>
            <p className="text-lg font-bold mb-6" style={{ color: "#242220" }}>
              {course?.course_name || decodedId}
            </p>

            {course?.certificate_number && (
              <div
                className="flex items-center justify-center gap-2 p-4 mb-6"
                style={{
                  background: "#FDF5EB",
                  borderRadius: "16px",
                  border: "1.5px solid #242220",
                }}
                data-testid="certificate-number"
              >
                <CheckCircle2 size={18} strokeWidth={2.5} color="#242220" />
                <div className="text-left">
                  <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.6 }}>
                    Bizonyítvány azonosítója
                  </p>
                  <p className="text-base font-bold" style={{ color: "#242220" }}>
                    {course.certificate_number}
                  </p>
                </div>
              </div>
            )}

            {course?.completion_date && (
              <p className="text-sm mb-6" style={{ color: "#242220", opacity: 0.5 }}>
                Teljesítés dátuma:{" "}
                {new Date(course.completion_date).toLocaleDateString("hu-HU")}
              </p>
            )}

            {error && (
              <div
                className="p-3 mb-4 text-sm font-bold"
                style={{
                  background: "#FDECEA",
                  border: "1.5px solid #D32F2F",
                  borderRadius: "12px",
                  color: "#D32F2F",
                }}
                data-testid="download-error"
              >
                {error}
              </div>
            )}

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full flex items-center justify-center gap-2 py-4 font-bold text-base transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
              style={{
                background: downloading ? "#e0c74a" : "#FBD556",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                boxShadow: downloading ? "2px 2px 0 #242220" : "6px 6px 0 #242220",
                color: "#242220",
                cursor: downloading ? "not-allowed" : "pointer",
              }}
              data-testid="download-certificate-btn"
            >
              <Download size={18} strokeWidth={2.5} />
              {downloading ? "Letöltés..." : "Bizonyítvány letöltése PDF-ben"}
            </button>

            <button
              onClick={() => navigate("/dashboard")}
              className="w-full mt-3 py-3 font-bold text-sm"
              style={{
                background: "transparent",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="back-to-dashboard-from-cert-btn"
            >
              Vissza a vezérlőpultra
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
